import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { confirmDialog } from 'primereact/confirmdialog';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

import {
  groupedTriggers,
  automationModules,
  invoiceDaysPastDue,
  paymentDaysPastDue,
  delayCardOptions,
  filterMap,
} from 'constants/automation';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { getFilterOptions } from 'modules/automation/utils/getFilterOptions';

export default function TriggerBlock() {
  const { automationRuleId } = useParams();
  const {
    control,
    watch,
    formState: { errors },
    resetField,
    getValues,
    setValue,
  } = useFormContext();
  const trigger = watch('trigger');

  // Function to confirm trigger change and remove incompatible filters and actions
  const confirmTriggerChange = (callBack, trigger) => {
    const actions = getValues('actions') || [];
    const filters = getValues('filters') || [];

    const filterOptions = getFilterOptions(trigger?.module, trigger?.code).map((f) => f.value) || [];

    const allowedCodes = filterMap[trigger?.code] || [];

    const cleanUpFields = (fieldName, original, allowedCheck) => {
      const kept = [];
      original.forEach((item, index) => {
        if (allowedCheck(item)) {
          kept.push(item);
        } else {
          resetField(`${fieldName}[${index}]`);
        }
      });
      setValue(fieldName, kept);
    };

    confirmDialog({
      message: 'Changing the trigger may remove some of the filters and actions from the workflow. Are you sure you want to proceed?',
      header: 'Confirm Trigger Change',
      icon: 'pi pi-exclamation-triangle',
      rejectClassName: 'p-button-secondary outline',
      accept: () => {
        callBack();
        cleanUpFields('actions', actions, (v) => allowedCodes.includes(v.action_code));
        cleanUpFields('filters', filters, (v) => filterOptions.includes(v.label));
      },
    });
  };

  return (
    <div className="card-bg p-3 trigger-section">
      <div className="d-flex align-items-start mb-3 mt-1">
        <i className="fas fa-bolt text-primary me-2 mt-1"></i>
        <div>
          <h6 className="mb-1 sub-title text-gray-500">Trigger</h6>
          <p className="card-text small text-muted mb-0">What starts this workflow?</p>
        </div>
      </div>

      <div className="col-12 col-lg-6">
        <div className="form-text small">
          Choose the event that will start this workflow <span className="text-danger">*</span>
        </div>
        <Controller
          name="trigger"
          control={control}
          rules={{ required: 'Trigger is required' }}
          render={({ field }) => (
            <Dropdown
              options={groupedTriggers}
              optionLabel="label"
              optionGroupLabel="label"
              optionGroupChildren="items"
              filterPlaceholder="Search"
              resetFilterOnHide={true} //clearing search keyword on hide
              className="w-100 input-shadow autowidth"
              panelClassName="grouped-dropdown"
              filter
              placeholder="Select Trigger"
              value={field.value}
              onChange={(e) => {
                const callBack = () => {
                  resetField('past_due_delay');
                  resetField('is_lead_automation');
                  resetField('count');
                  resetField('period');
                  field.onChange(e.value);
                };
                if (field.value) {
                  confirmTriggerChange(callBack, e.value);
                } else {
                  callBack();
                }
              }}
              optionValue={(v) => ({ label: v?.label, code: v?.code, module: v?.module })}
              inputRef={field.ref}
              disabled={automationRuleId && trigger?.code === 'TRIG_REVIEW_REQUEST'}
            />
          )}
        />
        <RequiredValidation errors={errors} field="trigger" />
        {automationRuleId && trigger?.code === 'TRIG_REVIEW_REQUEST' && (
          <div className="d-flex  files-note mt-2 align-items-center">
            <i className="pi pi-info-circle me-2" style={{ paddingTop: '2px' }}></i>
            Trigger cannot be updated for Review Request rules.
          </div>
        )}
      </div>

      {trigger?.module && ![automationModules.CASES, automationModules.LEADS, automationModules.BILLING]?.includes(trigger?.module) && (
        <div className="d-flex py-2 flex-column flex-lg-row flex-wrap">
          <div className="form-text small me-2">This is an automation for Leads</div>
          <Controller
            name="is_lead_automation"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <InputSwitch checked={Boolean(value)} name={name} onChange={(e) => onChange(e.value)} className="input-swich" />
            )}
          />
        </div>
      )}
      {(trigger?.code === 'TRIG_PAYMENT_REQUEST_PASTDUE' || trigger?.code === 'TRIG_INVOICE_PASTDUE') && (
        <div className="col-sm-12 gap-2 align-items-center py-2 flex-wrap">
          <div className="p-medium">
            No. of Days Past Due<span className="text-danger">*</span>
          </div>
          <Controller
            name={'past_due_delay'}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field: { name, value, onChange, ref } }) => (
              <Dropdown
                value={value}
                name={name}
                options={trigger?.code === 'TRIG_PAYMENT_REQUEST_PASTDUE' ? paymentDaysPastDue : invoiceDaysPastDue}
                onChange={(e) => onChange(e.value)}
                optionLabel="label"
                className="input-shadow autowidth"
                placeholder="Select Days"
                inputRef={ref}
              />
            )}
          />
          <RequiredValidation errors={errors} field={`past_due_delay`} />
        </div>
      )}

      {trigger?.code === 'TRIG_REVIEW_REQUEST' && (
        <div className="col-sm-12 d-flex align-items-center py-2 flex-column flex-lg-row flex-wrap">
          <div className="col-12 col-lg-8">
            <label className="p-medium" htmlFor="count">
              Time Before Execution
              <span className="text-danger">*</span>
            </label>
            <div className="d-flex align-items-center gap-2">
              <div>
                <Controller
                  name={'count'}
                  control={control}
                  rules={{ required: 'Count is required' }}
                  render={({ field: { name, value, onChange, ref }, fieldState: { error } }) => (
                    <InputNumber
                      value={value}
                      name={name}
                      className={`input-shadow ${error ? 'is-invalid' : ''}`}
                      onValueChange={(e) => onChange(e.value)}
                      min={1}
                      placeholder="Type in count"
                      ref={ref}
                    />
                  )}
                />
                <RequiredValidation errors={errors} field={`count`} />
              </div>
              <div>
                <Controller
                  name={'period'}
                  control={control}
                  rules={{ required: 'Period is required' }}
                  render={({ field: { name, value, onChange, ref }, fieldState: { error } }) => (
                    <Dropdown
                      value={value}
                      name={name}
                      options={delayCardOptions}
                      className={`input-shadow mw-100 ${error ? 'is-invalid' : ''}`}
                      placeholder="Select"
                      onChange={(e) => onChange(e.value)}
                      inputRef={ref}
                    />
                  )}
                />
                <RequiredValidation errors={errors} field={`period`} />
              </div>
            </div>
          </div>
        </div>
      )}

      {trigger?.code === 'TRIG_CASE_DATA_ENTRY' && (
        <div className="d-flex mt-2 files-note align-items-center">
          <i className="pi pi-info-circle me-1"></i>
          Excludes cases created by converting a lead
        </div>
      )}
    </div>
  );
}
